/**
 * Subscription billing DTOs shared by the API and the app.
 *
 * Stripe invoice objects are large and carry platform detail (price ids, account internals) that no
 * tenant should read, so the server projects each one down to this shape and the client only ever
 * sees this. Amounts stay in CENTS, the unit Stripe uses, and are formatted at the edge.
 */
/** The statuses a subscription invoice can actually reach. */
export const INVOICE_STATUSES = ['draft', 'open', 'paid', 'uncollectible', 'void'];
export function invoiceState(inv) {
    if (inv.status === 'paid')
        return 'paid';
    if (inv.status === 'void')
        return 'void';
    if (inv.status === 'draft')
        return 'draft';
    // uncollectible means Stripe has given up retrying, which is as late as it gets.
    if (inv.status === 'uncollectible')
        return 'late';
    // An open invoice that already has a scheduled retry has been attempted and refused at least once.
    return inv.next_payment_attempt ? 'late' : 'due';
}
/** Whether there is still money owed on this invoice, so the UI can offer to pay it. */
export function isInvoicePayable(inv) {
    const state = invoiceState(inv);
    return (state === 'late' || state === 'due') && inv.amount_due > inv.amount_paid;
}
/** The amount still owed, in cents (0 once settled). */
export function invoiceBalance(inv) {
    return Math.max(0, inv.amount_due - inv.amount_paid);
}
// ── Workspace access (the paywall) ──────────────────────────────────────────────────────────────────
// ONE rule, shared by the app's paywall AND the API's write gate, so the two can never disagree about
// whether a workspace is open.
const DAY = 86_400_000;
/** A trial keeps going without a card for this long after signup. */
export const CARD_REQUIRED_AFTER_DAYS = 15;
/** A declined renewal gets a full month to sort the card out before the workspace locks. */
export const PAYMENT_GRACE_DAYS = 30;
/** Stripe statuses that mean "we tried to charge and it did not go through". */
export const isDelinquent = (b) => b.subscription_status === 'past_due' || b.subscription_status === 'unpaid';
/** When a card becomes required to keep the trial going (signup + CARD_REQUIRED_AFTER_DAYS). */
export function cardRequiredAt(b) {
    return b.created_at ? new Date(b.created_at).getTime() + CARD_REQUIRED_AFTER_DAYS * DAY : null;
}
/** When the grace window after a failed payment runs out, or null when there's no failed payment. */
export function graceEndsAt(b) {
    if (!b || !isDelinquent(b) || !b.past_due_since)
        return null;
    return new Date(b.past_due_since).getTime() + PAYMENT_GRACE_DAYS * DAY;
}
/** Access: active sub, or an unexpired trial — but after day 15 a card is required to continue.
 *  A FAILED PAYMENT is not an ended subscription: the workspace stays open for PAYMENT_GRACE_DAYS
 *  so the team can fix the card (Stripe is retrying on its own the whole time).
 *  Testing accounts (e.g. @studiollama.com) always have access — they never hit the paywall. */
export function hasAccess(b, isTesting = false, now = Date.now()) {
    if (isTesting)
        return true;
    if (!b)
        return true; // still loading
    if (b.subscription_status === 'active')
        return true;
    if (b.subscription_status === 'trialing') {
        const trialEnd = b.trial_ends_at ? new Date(b.trial_ends_at).getTime() : Infinity;
        if (now > trialEnd)
            return false;
        if (b.stripe_subscription_id)
            return true; // card already on file → full trial
        const cardBy = cardRequiredAt(b);
        return cardBy == null || now <= cardBy;
    }
    if (isDelinquent(b)) {
        const grace = graceEndsAt(b);
        // An undated failure (webhook missed, or a row predating the grace clock) resolves in the paying
        // customer's favour: keep them working and let the alert chase the card. Locking a subscriber out
        // over our own missing timestamp is the worse failure.
        return grace == null || now <= grace;
    }
    return false;
}
//# sourceMappingURL=billing.js.map